/**
 * Xuvoria storefront enhancements.
 *
 * Kept outside the core Horizon variant snippet so Shopify can continue to use
 * its native picker markup and rerendering behavior. Enhancements are applied
 * idempotently after initial load and after section/variant DOM updates.
 *
 * Availability states are managed exclusively by Shopify Liquid and the
 * native variant-picker.js morph cycle. This file does not read, write, or
 * block variant availability.
 */
(() => {
  const MOBILE_LIMIT = 8;
  const DESKTOP_LIMIT = 10;

  const enhanceFieldset = (fieldset) => {
    const labels = Array.from(
      fieldset.querySelectorAll(':scope > label.variant-option__button-label')
    );

    labels.forEach((label, index) => {
      label.classList.toggle(
        'xuvoria-variant-option--mobile-overflow',
        index >= MOBILE_LIMIT
      );
      label.classList.toggle(
        'xuvoria-variant-option--desktop-overflow',
        index >= DESKTOP_LIMIT
      );
    });

    const hasMobileOverflow = labels.length > MOBILE_LIMIT;
    const hasDesktopOverflow = labels.length > DESKTOP_LIMIT;
    let toggle = fieldset.querySelector(
      ':scope > [data-xuvoria-variant-toggle]'
    );

    fieldset.classList.toggle(
      'xuvoria-variant-overflow',
      hasMobileOverflow
    );
    fieldset.classList.toggle(
      'xuvoria-variant-overflow--desktop',
      hasDesktopOverflow
    );
    fieldset.classList.toggle(
      'xuvoria-variant-overflow--mobile-only',
      hasMobileOverflow && !hasDesktopOverflow
    );

    if (!hasMobileOverflow) {
      delete fieldset.dataset.xuvoriaExpanded;
      toggle?.remove();
      return;
    }

    if (!fieldset.dataset.xuvoriaExpanded) {
      fieldset.dataset.xuvoriaExpanded = 'false';
    }

    const legend = fieldset.querySelector(':scope > legend');
    const legendText = (
      legend?.childNodes?.[0]?.textContent ||
      legend?.textContent ||
      ''
    ).toLowerCase();

    const noun =
      legendText.includes('color') || legendText.includes('colour')
        ? 'colors'
        : 'options';

    if (!toggle) {
      toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'xuvoria-variant-overflow__toggle';
      toggle.dataset.xuvoriaVariantToggle = '';
      fieldset.append(toggle);
    }

    toggle.setAttribute(
      'aria-expanded',
      fieldset.dataset.xuvoriaExpanded === 'true' ? 'true' : 'false'
    );

    toggle.innerHTML = `
      <span class="xuvoria-variant-overflow__show">View all ${labels.length} ${noun}</span>
      <span class="xuvoria-variant-overflow__hide">Show fewer ${noun}</span>
    `;
  };

  const enhanceVariantPicker = (picker) => {
    picker
      .querySelectorAll('fieldset.variant-option--buttons')
      .forEach(enhanceFieldset);
  };

  const hideEmptyReviewMessages = (root = document) => {
    const scope = root instanceof Element ? root : document;
    const appRoots = [];

    if (
      scope instanceof Element &&
      scope.matches('.shopify-app-block, [class*="jdgm"], [id*="jdgm"]')
    ) {
      appRoots.push(scope);
    }

    scope
      .querySelectorAll?.(
        '.shopify-app-block, [class*="jdgm"], [id*="jdgm"]'
      )
      .forEach((element) => appRoots.push(element));

    appRoots.forEach((appRoot) => {
      appRoot.querySelectorAll('*').forEach((element) => {
        if (element.childElementCount > 0) return;

        const text = (element.textContent || '')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();

        if (text === 'no items found') {
          element.hidden = true;
          element.setAttribute('aria-hidden', 'true');
          element.classList.add('xuvoria-review-empty-message');
        }
      });
    });
  };

  const enhanceStorefront = (root = document) => {
    const pickers = [];

    if (root instanceof Element && root.matches('variant-picker')) {
      pickers.push(root);
    }

    root
      .querySelectorAll?.('variant-picker')
      .forEach((picker) => pickers.push(picker));

    pickers.forEach(enhanceVariantPicker);
    hideEmptyReviewMessages(root);
  };

  let enhancementQueued = false;

  const queueEnhancement = (root = document) => {
    if (enhancementQueued) return;

    enhancementQueued = true;

    requestAnimationFrame(() => {
      enhancementQueued = false;
      enhanceStorefront(root);
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const toggle = event.target.closest(
        '[data-xuvoria-variant-toggle]'
      );

      if (!toggle) return;

      if (toggle.hasAttribute('data-xuvoria-inline-toggle')) {
        return;
      }

      const fieldset = toggle.closest('.xuvoria-variant-overflow');

      if (!fieldset) return;

      const expanded =
        fieldset.dataset.xuvoriaExpanded === 'true';

      fieldset.dataset.xuvoriaExpanded = expanded
        ? 'false'
        : 'true';

      toggle.setAttribute(
        'aria-expanded',
        expanded ? 'false' : 'true'
      );
    },
    true
  );

  document.addEventListener('DOMContentLoaded', () => {
    enhanceStorefront();

    const observer = new MutationObserver((mutations) => {
      const hasRelevantChange = mutations.some((mutation) => {
        if (mutation.type === 'attributes') {
          return (
            mutation.target instanceof Element &&
            Boolean(mutation.target.closest('variant-picker'))
          );
        }

        return Array.from(mutation.addedNodes).some(
          (node) => node instanceof Element
        );
      });

      if (hasRelevantChange) {
        queueEnhancement();
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-option-available'],
    });
  });

  document.addEventListener(
    'shopify:section:load',
    () => queueEnhancement()
  );

  document.addEventListener(
    'shopify:block:select',
    () => queueEnhancement()
  );

  window.addEventListener('pageshow', () => queueEnhancement());
})();